import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Button,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Snackbar,
  Breadcrumbs,
  Link
} from '@mui/material';
import PageHeader from '../../components/layout/PageHeader';
import {
  Add as AddIcon,
  Category as CategoryIcon
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import CategoryForm from './CategoryForm';
import CategoryList from './CategoryList';
import { useCategories } from '../../hooks/useCategories';
import { HomeExpenseGroupCategoryDto, ExpenseType } from '../../types/homeExpenses';
import { getHomeExpenseGroups } from '../../services/homeExpensesApi';

const CategoriesPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    categories,
    loading,
    error,
    fetchCategories,
    createNewCategory,
    updateExistingCategory,
    deleteExistingCategory,
  } = useCategories();

  const [showForm, setShowForm] = useState(false);
  const [editingCategory, setEditingCategory] = useState<HomeExpenseGroupCategoryDto | null>(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [categoryToDelete, setCategoryToDelete] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [groupsLoading, setGroupsLoading] = useState(false);
  const [categoryIdsWithGroups, setCategoryIdsWithGroups] = useState<Set<string>>(new Set());

  // Detect expense type from route
  const expenseType = useMemo(() => 
    location.pathname.includes('personal') ? ExpenseType.Personal : ExpenseType.Home,
    [location.pathname]
  );

  // Filter categories to only show those with groups of the current type
  const filteredCategories = useMemo(() => {
    if (groupsLoading) {
      // Still loading groups, show all categories temporarily
      return categories;
    }
    // After loading, only show categories that have at least one group of the current type
    // If no groups exist for this type, categoryIdsWithGroups will be empty and no categories will show
    return categories.filter(cat => categoryIdsWithGroups.has(cat.publicId));
  }, [categories, categoryIdsWithGroups, groupsLoading]);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  // Fetch groups for the current expense type to determine which categories to show
  useEffect(() => {
    const fetchGroups = async () => {
      setGroupsLoading(true);
      try {
        const groups = await getHomeExpenseGroups(expenseType);
        const categoryIds = new Set<string>();
        groups.forEach(group => {
          if (group.category?.publicId) {
            categoryIds.add(group.category.publicId);
          }
        });
        setCategoryIdsWithGroups(categoryIds);
      } catch (err) {
        console.error('Failed to fetch groups for category filtering:', err);
      } finally {
        setGroupsLoading(false);
      }
    };
    fetchGroups();
  }, [expenseType]);

  const handleCreate = () => {
    setEditingCategory(null);
    setShowForm(true);
  };

  const handleEdit = (category: HomeExpenseGroupCategoryDto) => {
    setEditingCategory(category);
    setShowForm(true);
  };

  const handleFormSubmit = async (data: {
    name: string;
    description?: string;
    color?: string;
    sortOrder: number;
  }) => {
    let success = false;
    
    if (editingCategory) {
      success = await updateExistingCategory(editingCategory.publicId, data);
      if (success) {
        setSuccessMessage('Categoria atualizada com sucesso');
      }
    } else {
      success = await createNewCategory(data);
      if (success) {
        setSuccessMessage('Categoria criada com sucesso');
      }
    }
    
    if (success) {
      setShowForm(false);
      setEditingCategory(null);
    }
  };

  const handleDelete = (categoryId: string) => {
    setCategoryToDelete(categoryId);
    setShowDeleteDialog(true);
  };

  const confirmDelete = async () => {
    if (categoryToDelete) {
      const success = await deleteExistingCategory(categoryToDelete);
      if (success) {
        setSuccessMessage('Categoria eliminada com sucesso');
      }
    }
    setShowDeleteDialog(false);
    setCategoryToDelete(null);
  };

  const handleCloseForm = () => {
    setShowForm(false);
    setEditingCategory(null);
  };

  return (
    <Box>
      {/* Breadcrumbs */}
      <Breadcrumbs sx={{ mb: 2 }}>
        <Link 
          color="inherit" 
          href="#" 
          onClick={(e) => { e.preventDefault(); navigate('/'); }}
        >
          Início
        </Link>
        <Link 
          color="inherit" 
          href="#" 
          onClick={(e) => { e.preventDefault(); navigate('/expenses'); }}
        >
          Despesas
        </Link>
        <Typography color="text.primary">Categorias</Typography>
      </Breadcrumbs>

      {/* Header */}
      <PageHeader
        title="Categorias de grupos de despesas"
        subtitle="Organize os seus grupos de despesas criando categorias. As categorias ajudam a agrupar tipos de despesa relacionados para uma melhor organização e relatórios."
        icon={<CategoryIcon color="primary" />}
        actions={
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleCreate}
            disabled={loading}
          >
            Adicionar categoria
          </Button>
        }
      />

      {/* Info Alert showing current mode */}
      <Alert severity="info" sx={{ mb: 3 }}>
        A mostrar categorias de <strong>despesas {expenseType === ExpenseType.Personal ? 'pessoais' : 'da casa'}</strong>.
        Só são apresentadas as categorias com pelo menos um grupo deste tipo.
      </Alert>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Category List */}
      <CategoryList
        categories={filteredCategories}
        onEdit={handleEdit}
        onDelete={handleDelete}
        loading={loading || groupsLoading}
      />

      {/* Form Dialog */}
      <Dialog 
        open={showForm} 
        onClose={handleCloseForm}
        maxWidth="sm"
        fullWidth
      >
        <DialogContent>
          <CategoryForm
            initial={editingCategory || undefined}
            onSubmit={handleFormSubmit}
            onCancel={handleCloseForm}
            loading={loading}
          />
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={showDeleteDialog}
        onClose={() => setShowDeleteDialog(false)}
      >
        <DialogTitle>Eliminar categoria</DialogTitle>
        <DialogContent>
          <Typography>
            Tem a certeza de que pretende eliminar esta categoria? Esta ação não pode ser anulada.
            Nota: as categorias com grupos de despesas associados não podem ser eliminadas.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowDeleteDialog(false)}>
            Cancelar
          </Button>
          <Button 
            onClick={confirmDelete} 
            color="error" 
            variant="contained"
            disabled={loading}
          >
            Eliminar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success Snackbar */}
      <Snackbar
        open={!!successMessage}
        autoHideDuration={4000}
        onClose={() => setSuccessMessage(null)}
      >
        <Alert 
          onClose={() => setSuccessMessage(null)} 
          severity="success"
          sx={{ width: '100%' }}
        >
          {successMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CategoriesPage;
